import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaCheckCircle, FaWhatsapp, FaCar, FaLock, FaUserCircle, FaPalette } from 'react-icons/fa';
import { MdRocketLaunch } from 'react-icons/md';
import api from '../../services/api';
import { useAppSelector } from '../../store/hooks';
import { obterIdTenent, obterNomeEmpresa } from '../../services/seguranca';
import { rastrearEventoFunil, enviarFilaFunil } from '../../services/rastreamentoFunil';

const AZUL = '#1F467E';
const AZUL_CLARO = '#3b82f6';
const VERDE_WPP = '#25d366';

interface VeiculoPreview {
    imagem: string | null;
    marca: string;
    modelo: string;
}

const VEICULOS_ILUSTRATIVOS: VeiculoPreview[] = [
    { imagem: null, marca: 'Chevrolet', modelo: 'Onix' },
    { imagem: null, marca: 'Hyundai', modelo: 'HB20' },
    { imagem: null, marca: 'Honda', modelo: 'CG 160' },
];

/* ---------------------------------- animações ---------------------------------- */

const flutuar = keyframes`
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-8px); }
`;

const brilho = keyframes`
    0% { transform: translateX(-120%) skewX(-18deg); }
    60%, 100% { transform: translateX(240%) skewX(-18deg); }
`;

const surgir = keyframes`
    from { opacity: 0; transform: translateY(14px); }
    to { opacity: 1; transform: translateY(0); }
`;

/* ----------------------------------- layout ------------------------------------ */

const Wrapper = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
    gap: 48px;
    align-items: center;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    padding: 48px 52px;
    animation: ${surgir} 0.45s ease both;
    overflow: hidden;
    position: relative;

    &::before {
        content: '';
        position: absolute;
        top: -180px;
        right: -180px;
        width: 420px;
        height: 420px;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(59, 130, 246, 0.10) 0%, transparent 70%);
        pointer-events: none;
    }

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
        gap: 36px;
        padding: 32px 24px;
    }
`;

const Coluna = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 18px;
    min-width: 0;
`;

const BadgeNovo = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 999px;
    background: linear-gradient(90deg, rgba(31, 70, 126, 0.08), rgba(59, 130, 246, 0.12));
    color: ${AZUL};
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

const Titulo = styled.h1`
    font-size: clamp(1.7rem, 3vw, 2.35rem);
    font-weight: 800;
    line-height: 1.15;
    color: #0f172a;
    margin: 0;

    b {
        font-weight: 800;
        background: linear-gradient(120deg, rgba(59, 130, 246, 0.22) 0%, rgba(31, 70, 126, 0.22) 100%);
        border-radius: 6px;
        padding: 0 6px;
    }
`;

const Subtitulo = styled.p`
    font-size: 15.5px;
    color: #475569;
    margin: 0;
    max-width: 460px;
    line-height: 1.55;
`;

const ListaBeneficios = styled.ul`
    list-style: none;
    padding: 0;
    margin: 4px 0 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const Beneficio = styled.li`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 14.5px;
    color: #1e293b;
    line-height: 1.45;

    svg {
        color: #22c55e;
        flex-shrink: 0;
        margin-top: 2px;
    }

    b { font-weight: 700; }
`;

const PrecoRow = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-top: 6px;
`;

const PrecoPrefixo = styled.span`
    font-size: 13px;
    color: #64748b;
`;

const PrecoValor = styled.span`
    font-size: 34px;
    font-weight: 800;
    color: ${AZUL};
    letter-spacing: -0.02em;
`;

const PrecoSufixo = styled.span`
    font-size: 15px;
    color: #64748b;
    font-weight: 600;
`;

const BotaoQuero = styled.button`
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 16px 38px;
    border: none;
    border-radius: 999px;
    background: linear-gradient(135deg, ${AZUL} 0%, ${AZUL_CLARO} 100%);
    color: #fff;
    font-size: 17px;
    font-weight: 700;
    cursor: pointer;
    overflow: hidden;
    box-shadow: 0 14px 30px rgba(31, 70, 126, 0.32);
    transition: transform 0.18s ease, box-shadow 0.18s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 18px 38px rgba(31, 70, 126, 0.42);
    }

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 40%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.35), transparent);
        animation: ${brilho} 3.2s ease-in-out infinite;
    }
`;

/* ------------------------------- mockup do browser ------------------------------ */

const MockupArea = styled.div`
    position: relative;
    min-width: 0;
`;

const Browser = styled.div`
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.16);
    overflow: hidden;
    animation: ${flutuar} 6s ease-in-out infinite;
`;

const BrowserBar = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: #f1f5f9;
    border-bottom: 1px solid #e2e8f0;
`;

const Dots = styled.div`
    display: flex;
    gap: 5px;

    span {
        width: 9px;
        height: 9px;
        border-radius: 50%;
    }
    span:nth-child(1) { background: #fda4af; }
    span:nth-child(2) { background: #fcd34d; }
    span:nth-child(3) { background: #86efac; }
`;

const AddressBar = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    padding: 5px 14px;
    font-size: 12px;
    color: #334155;
    font-weight: 500;
    min-width: 0;

    svg { color: #22c55e; flex-shrink: 0; }

    span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const MiniHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 12px 20px;
    border-bottom: 1px solid #f1f5f9;
`;

const MiniLogo = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 800;
    color: #0f172a;
    min-width: 0;

    img {
        height: 22px;
        max-width: 110px;
        object-fit: contain;
    }

    span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const MiniNav = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;

    > span:not(:last-child) {
        width: 34px;
        height: 8px;
        border-radius: 999px;
        background: #e2e8f0;
    }
`;

const MiniBotaoArea = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 12px;
    border-radius: 999px;
    border: 1.5px solid ${AZUL};
    color: ${AZUL};
    font-size: 10.5px;
    font-weight: 700;
    white-space: nowrap;
`;

const ChipPersonalizavel = styled.span`
    position: absolute;
    bottom: -14px;
    left: 14px;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 999px;
    background: #fff;
    border: 1px solid #e2e8f0;
    color: #0f172a;
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.03em;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.14);

    svg { color: ${AZUL_CLARO}; }
`;

const MiniHero = styled.div`
    padding: 22px 20px 18px;
`;

const MiniKicker = styled.div`
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${AZUL};
    margin-bottom: 6px;
`;

const MiniTitulo = styled.div`
    font-size: 19px;
    font-weight: 800;
    color: #0f172a;
    line-height: 1.2;
    margin-bottom: 12px;
`;

const MiniBotoes = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`;

const MiniBotaoWpp = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 16px;
    border-radius: 999px;
    background: ${VERDE_WPP};
    color: #fff;
    font-size: 11.5px;
    font-weight: 700;
    box-shadow: 0 8px 18px rgba(37, 211, 102, 0.32);
`;

const MiniBotaoFrota = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 7px 16px;
    border-radius: 999px;
    border: 1.5px solid #cbd5e1;
    color: #334155;
    font-size: 11.5px;
    font-weight: 700;
`;

const MiniFrota = styled.div`
    padding: 14px 20px 20px;
    background: #f6f8fb;
`;

const MiniFrotaTitulo = styled.div`
    font-size: 11px;
    font-weight: 700;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 10px;
`;

const GradeVeiculos = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
`;

const CardVeiculo = styled.div`
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    overflow: hidden;
`;

const FotoVeiculo = styled.div`
    aspect-ratio: 4 / 3;
    background: linear-gradient(135deg, #e8eef7 0%, #d7e2f2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const InfoVeiculo = styled.div`
    padding: 7px 9px 9px;
`;

const NomeVeiculo = styled.div`
    font-size: 10.5px;
    font-weight: 700;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ChipDisponivel = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 4px;
    font-size: 9px;
    font-weight: 600;
    color: #065f46;

    &::before {
        content: '';
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: #22c55e;
    }
`;

/* --------------------------------- componente ---------------------------------- */

function gerarSlug(nome: string): string {
    const slug = (nome || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '')
        .slice(0, 30);
    return slug || 'sualocadora';
}

export function UpsellSite() {
    const token = useAppSelector(state => state.token);
    const tenantId = obterIdTenent(token);
    const nomeEmpresa = obterNomeEmpresa(token);

    const [logoUrl, setLogoUrl] = useState<string>('');
    const [veiculos, setVeiculos] = useState<VeiculoPreview[]>([]);
    const [qtdFrota, setQtdFrota] = useState<number>(0);

    const slug = useMemo(() => gerarSlug(nomeEmpresa), [nomeEmpresa]);
    const frotaReal = veiculos.length > 0;
    const cards = frotaReal ? veiculos : VEICULOS_ILUSTRATIVOS;

    useEffect(() => {
        rastrearEventoFunil('visualizou_upsell_site', { origem: 'meu-site' }, { coalescer: true });

        api.get(`/Empresa/ObterURLLogo/${tenantId}`)
            .then(res => {
                if (typeof res.data === 'string' && res.data) setLogoUrl(res.data);
            })
            .catch(() => { /* sem logo, preview usa o nome da empresa */ });

        api.get(`/VeiculoPorEmpresaComArquivos/${tenantId}`)
            .then(res => {
                const ativos = (res.data || []).filter(
                    (v: any) => !v.vendido && !v.inativo && !v.roubado
                );
                setQtdFrota(ativos.length);
                const comFotoPrimeiro = [...ativos].sort(
                    (a: any, b: any) => (b.imagem ? 1 : 0) - (a.imagem ? 1 : 0)
                );
                setVeiculos(
                    comFotoPrimeiro.slice(0, 3).map((v: any) => ({
                        imagem: v.imagem || null,
                        marca: v.marca,
                        modelo: v.modelo,
                    }))
                );
            })
            .catch(() => { /* sem permissão de veículos, preview usa frota ilustrativa */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleQueroClick = () => {
        rastrearEventoFunil('clicou_quero_site', {
            origem: 'meu-site',
            qtdVeiculos: qtdFrota,
        });
        enviarFilaFunil();
        const mensagem = encodeURIComponent(
            'Olá! Quero o site para a minha locadora. Vi o preview no sistema e quero saber mais!'
        );
        window.open(`https://api.whatsapp.com/send?phone=5585991491356&text=${mensagem}`, '_blank');
    };

    return (
        <Wrapper>
            <Coluna>
                <BadgeNovo>✦ Novo · Site profissional</BadgeNovo>
                <Titulo>
                    O site da sua locadora, <b>pronto</b> para vender
                </Titulo>
                <Subtitulo>
                    Nossa equipe cria, escreve e publica tudo para você.
                    Seu único trabalho é compartilhar o link.
                </Subtitulo>
                <ListaBeneficios>
                    <Beneficio>
                        <FaCheckCircle size={15} />
                        <span><b>Você não se preocupa com nada</b>, entregamos tudo pronto</span>
                    </Beneficio>
                    <Beneficio>
                        <FaCheckCircle size={15} />
                        <span><b>Entrega rápida</b>, seu site no ar em 1 dia útil</span>
                    </Beneficio>
                    <Beneficio>
                        <FaCheckCircle size={15} />
                        <span><b>Visual personalizável</b>, com as suas cores e a sua logo</span>
                    </Beneficio>
                    <Beneficio>
                        <FaCheckCircle size={15} />
                        <span><b>Integrado ao sistema</b>, frota sincronizada automaticamente</span>
                    </Beneficio>
                    <Beneficio>
                        <FaCheckCircle size={15} />
                        <span><b>Disponibilidade em tempo real</b>, veículo alugado sai do site sozinho</span>
                    </Beneficio>
                    <Beneficio>
                        <FaCheckCircle size={15} />
                        <span><b>Área do cliente</b> com contratos, faturas e PIX para seus locatários</span>
                    </Beneficio>
                </ListaBeneficios>
                <PrecoRow>
                    <PrecoPrefixo>a partir de</PrecoPrefixo>
                    <PrecoValor>R$ 99,90</PrecoValor>
                    <PrecoSufixo>/mês</PrecoSufixo>
                </PrecoRow>
                <BotaoQuero type="button" onClick={handleQueroClick}>
                    <MdRocketLaunch size={20} />
                    Eu quero meu site!
                </BotaoQuero>
            </Coluna>

            <MockupArea>
                <ChipPersonalizavel>
                    <FaPalette size={12} />
                    Visual personalizável com as suas cores e logo
                </ChipPersonalizavel>
                <Browser>
                    <BrowserBar>
                        <Dots><span /><span /><span /></Dots>
                        <AddressBar>
                            <FaLock size={10} />
                            <span>{slug}.locasmartpro.com.br</span>
                        </AddressBar>
                    </BrowserBar>
                    <MiniHeader>
                        <MiniLogo>
                            {logoUrl
                                ? <img src={logoUrl} alt={nomeEmpresa} />
                                : <span>{nomeEmpresa}</span>}
                        </MiniLogo>
                        <MiniNav>
                            <span />
                            <MiniBotaoArea><FaUserCircle size={12} /> Área do Cliente</MiniBotaoArea>
                        </MiniNav>
                    </MiniHeader>
                    <MiniHero>
                        <MiniKicker>Aluguel de veículos</MiniKicker>
                        <MiniTitulo>Seu carro pronto para rodar hoje</MiniTitulo>
                        <MiniBotoes>
                            <MiniBotaoWpp><FaWhatsapp size={12} /> Falar no WhatsApp</MiniBotaoWpp>
                            <MiniBotaoFrota>Ver frota</MiniBotaoFrota>
                        </MiniBotoes>
                    </MiniHero>
                    <MiniFrota>
                        <MiniFrotaTitulo>Nossa frota</MiniFrotaTitulo>
                        <GradeVeiculos>
                            {cards.map((v, i) => (
                                <CardVeiculo key={i}>
                                    <FotoVeiculo>
                                        {v.imagem
                                            ? <img src={v.imagem} alt={`${v.marca} ${v.modelo}`} />
                                            : <FaCar size={22} />}
                                    </FotoVeiculo>
                                    <InfoVeiculo>
                                        <NomeVeiculo>{v.marca} {v.modelo}</NomeVeiculo>
                                        <ChipDisponivel>Disponível</ChipDisponivel>
                                    </InfoVeiculo>
                                </CardVeiculo>
                            ))}
                        </GradeVeiculos>
                    </MiniFrota>
                </Browser>
            </MockupArea>
        </Wrapper>
    );
}
